// src/pages/Training.js
import React, { useRef, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTools,
  faHourglassHalf,
  faBell,
  faRocket,
} from '@fortawesome/free-solid-svg-icons';
import { departmentsData } from '../data/departments';
import './Training.css';
import heroBg from '../images/formation_450x500_1920x780.webp';

const Training = () => {
  const { t } = useTranslation();
  const { department } = useParams();
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [activeCategory, setActiveCategory] = useState('all');
  const [trainings, setTrainings] = useState([]); //--- state défini ici

  const deptData = departmentsData[department];

  useEffect(() => {
    if (!deptData) {
      navigate('/');
      return;
    }
    // Traduire les formations
    const translatedTrainings = deptData.services.map(service => ({
      ...service,
      title: t(service.titleKey),
      description: t(service.descriptionKey),
      level: t(service.levelKey),
    }));
    setTrainings(translatedTrainings);
  }, [department, deptData, navigate, t]);

  const filteredTrainings = activeCategory === 'all'
    ? trainings
    : trainings.filter(training => training.category === activeCategory);

  if (!deptData) return null;

  // Catégories uniques basées sur les formations
  const categories = [
    { id: 'all', label: t('training_all_categories') },
    ...Array.from(new Set(trainings.map(t => t.category || 'autre'))).map(cat => ({
      id: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1)
    }))
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const durationLabel = t('training_duration_months');

  return (
    <div className="training-page">
      {/* ===== HERO ===== */}
      <section 
        className="training-hero"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="training-hero-overlay"></div>
        <div className="hero-particles">
          <span></span><span></span><span></span><span></span>
          <span></span><span></span><span></span><span></span>
        </div>
        <div className="container">
          <motion.div
            className="training-hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="badge-dot"></span>
              {t(deptData.heroBadgeKey)}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {t(deptData.titleKey)}
            </motion.h1>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {t(deptData.subtitleKey)}
            </motion.p>

            <motion.div
              className="training-breadcrumb"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <span><a href="/">{t('menu_Accueil')}</a></span>
              <span className="separator">›</span>
              <span><a href={`/${department}`}>{t(deptData.breadcrumbLabelKey)}</a></span>
              <span className="separator">›</span>
              <span className="current">{t('Formation')}</span>
            </motion.div>

            <div className="hero-line-wrapper">
              <div className="hero-line" style={{ background: `linear-gradient(90deg, transparent 0%, ${deptData.color} 50%, transparent 100%)` }}></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SECTION FORMATIONS ===== */}
      <section className="training-section" ref={ref}>
        <div className="container">
          <motion.div
            className="training-section-header"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h3 style={{ color: deptData.color }}>{t('training_section_title')}</h3>
            <h2>{t('training_section_subtitle')}</h2>
          </motion.div>

          {/* Catégories */}
          <motion.div
            className="training-categories"
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                style={activeCategory === cat.id ? {
                  background: deptData.color,
                  borderColor: deptData.color
                } : {}}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* Grille des formations */}
          <motion.div
            className="training-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {filteredTrainings.map((training) => (
              <motion.div
                key={training.id}
                className="training-card"
                variants={itemVariants}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="training-card-icon"
                  style={{
                    backgroundColor: deptData.color + '15',
                    color: deptData.color
                  }}
                >
                  <FontAwesomeIcon icon={training.icon} />
                </div>
                <h3>{training.title}</h3>
                <p>{training.description}</p>
                <div className="training-card-meta">
                  <span className="meta-duration">{training.duration} {durationLabel}</span>
                  <span
                    className="meta-level"
                    style={{
                      backgroundColor: deptData.color + '15',
                      color: deptData.color
                    }}
                  >
                    {training.level}
                  </span>
                </div>
                <Link to="#notify" className="training-card-btn" style={{ color: deptData.color }}>
                  {t('training_btn_notify')}
                  <span className="btn-arrow">→</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Message "Aucune formation" */}
          {filteredTrainings.length === 0 && (
            <div className="no-trainings">
              <p>{t('training_no_results')}</p>
            </div>
          )}

          {/* ===== CARTE "FORMATIONS EN PRÉPARATION" ===== */}
          <motion.div
            className="training-preparation"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="preparation-card" style={{ borderColor: deptData.color }}>
              <div className="preparation-header" style={{ background: `linear-gradient(135deg, ${deptData.color}, ${deptData.color}dd)` }}>
                <FontAwesomeIcon icon={faTools} className="preparation-icon" />
                <h3>{t('msg_prepa_formation_depart_Info')}</h3>
              </div>
              <div className="preparation-body">
                <div className="preparation-message">
                  <FontAwesomeIcon icon={faHourglassHalf} className="hourglass-icon" style={{ color: deptData.color }} />
                  <p>{t('msg2_prepa_formation_depart_Info')}</p>
                </div>
                <p className="preparation-text">{t('msg3_prepa_formation_depart_Info')}</p>
                <div className="preparation-progress">
                  <div className="progress-bar" style={{ width: '40%', background: `linear-gradient(90deg, ${deptData.color}, ${deptData.color}dd)` }}>
                    <span>{t('msg4_prepa_formation_depart_Info')}</span>
                  </div>
                </div>
                <button className="preparation-btn" style={{ background: deptData.color }}>
                  <FontAwesomeIcon icon={faBell} />
                  {t('btn_msg4_prepa_formation_depart_Info')}
                </button>
              </div>
              <div className="preparation-footer">
                <FontAwesomeIcon icon={faRocket} style={{ color: deptData.color }} />
                <span>{t('signature_prepa_formation_depart_Info')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Training;